import { Component, Host, h, State, getAssetPath, Prop } from "@stencil/core";
import * as THREE from "three";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader";
import {
  IElevatorMaterials,
  ILiftStatusSensorDataResponse,
  ILiftStatusWebComponentError,
  TMovementSensor,
} from "../../utils/types";
import * as TWEEN from "@tweenjs/tween.js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import "dayjs/locale/nl-be";
import { getStore, ILiftStatusState } from "../../store";
import { isEmpty } from "lodash-es";
import { ObservableMap } from "@stencil/store";
dayjs.locale("nl-be");

@Component({
  tag: "ls-3d-scene",
  styleUrl: "ls-3d-scene.css",
  shadow: true,
  assetsDirs: ["assets"],
})
export class Ls3dScene {
  /**
   * Elevator state (movement)
   */
  @State() movementState: TMovementSensor;

  /**
   * Current stop
   */
  @State() currentStop: number;

  /**
   * Last passed stop
   */
  @State() lastPassedStop: number;

  /**
   * The timestamp of the latest sensor update
   */
  @State() lastUpdate: number;

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  private store: ObservableMap<ILiftStatusState>;

  private animateTheFloor = true;
  private arrowDown;
  private arrowUp;
  private backDoor;
  private backDoorLeft = new THREE.Object3D();
  private backDoorRight = new THREE.Object3D();
  private camera: THREE.PerspectiveCamera;
  private canvas: HTMLDivElement;
  private doorWidth = 12.5;
  private elevator: THREE.Scene;
  private elevatorBackWall;
  private elevatorBackWallBeam;
  private floorAnimationTween: TWEEN.Tween<{ topPos: number }> | null = null;
  private floorHeight = 500;
  private floorSeparatorIndicator = new THREE.Group();
  private frontDoorLeft = new THREE.Object3D();
  private frontDoorRight = new THREE.Object3D();
  private movingDirection: "up" | "down";
  private pulseInterval: NodeJS.Timer | null = null;
  private renderer: THREE.WebGLRenderer;
  private scene;
  private speed = 3000;

  private positions = {
    frontDoor: {
      left: {
        open: 0,
        closed: 0,
      },
      right: {
        open: 0,
        closed: 0,
      },
    },
    backDoor: {
      left: {
        open: 0,
        closed: 0,
      },
      right: {
        open: 0,
        closed: 0,
      },
    },
    floor: {
      down: 0,
      up: 0,
    },
  };

  private materials: IElevatorMaterials = {
    active: new THREE.MeshStandardMaterial({ color: 0xff0000, opacity: 1 }),
    normal: new THREE.MeshStandardMaterial({
      color: 0xa3a3a3,
      opacity: 1,
      transparent: false,
    }),
    pulseOn: new THREE.MeshStandardMaterial({
      color: 0x024970,
      opacity: 0.5,
      transparent: true,
    }),
    pulseOff: new THREE.MeshStandardMaterial({
      color: 0x024970,
      opacity: 1,
      transparent: false,
    }),
    transparent: new THREE.MeshStandardMaterial({
      color: 0xffffff,
      opacity: 0,
      transparent: true,
    }),
    floor: new THREE.MeshStandardMaterial({
      color: 0x85b19b,
      opacity: 0.15,
      transparent: true,
    }),
    door: new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0x0,
      roughness: 0,
      metalness: 1,
    }),
  };

  private initializeScene = () => {
    // Setup the camera
    this.camera = new THREE.PerspectiveCamera(45, 500 / 500, 1, 1000);
    this.camera.position.set(7, 4, 3.5);
    this.camera.zoom = 1.7;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(0, 0, 0);

    // Create the scene
    this.scene = new THREE.Scene();

    // Initialize the loading manager
    const loadingManager = new THREE.LoadingManager(() => {
      // If 3D scene file failed to load
      if (this.elevator === undefined) {
        const error = document.createElement("div");
        error.innerHTML = "Failed to load 3D scene file";
        error.classList.add("failed-to-load-error");

        // On reload: replace the current scene, instead of creating a new one
        if (this.canvas.children.length > 2) {
          this.canvas.replaceChild(error, this.canvas.children[2]);
        } else {
          this.canvas.appendChild(error);
        }
      } else {
        this.scene.add(this.elevator);
      }
    });

    // Load the elevator.dae file and initialize the objects
    const loader = new ColladaLoader(loadingManager);
    loader.load(getAssetPath("./assets/elevator-001.dae"), (collada) => {
      this.elevator = collada.scene;

      this.arrowUp = this.elevator.getObjectByName("arrow_up");
      this.arrowDown = this.elevator.getObjectByName("arrow_down");
      this.elevatorBackWall =
        this.elevator.getObjectByName("elevator_back_wall");
      this.elevatorBackWallBeam =
        this.elevator.getObjectByName("achter_wand_beam");
      this.frontDoorLeft = this.elevator.getObjectByName("front_door_left");
      this.frontDoorRight = this.elevator.getObjectByName("front_door_right");
      this.backDoor = this.elevator.getObjectByName("back_door");
      this.backDoorLeft = this.elevator.getObjectByName("back_door_left");
      this.backDoorRight = this.elevator.getObjectByName("back_door_right");
      this.floorSeparatorIndicator = this.elevator.getObjectByName(
        "floor_separator_indicator"
      ) as THREE.Group;

      // Hide outlines
      this.elevator.children[0].traverse((childNode: THREE.LineSegments) => {
        if (childNode.type === "LineSegments") {
          childNode.visible = false;
        }
      });

      // Change the material for the arrows
      for (let i = 0; i < this.arrowDown.children.length; i++) {
        this.changeMaterial(
          this.arrowDown.children[i],
          this.materials.pulseOff
        );
        this.changeMaterial(this.arrowUp.children[i], this.materials.pulseOff);
      }

      // Hide arrows
      this.stopMoving();

      // Assign positions
      this.positions = {
        frontDoor: {
          left: {
            open: this.frontDoorLeft.position.y + this.doorWidth,
            closed: this.frontDoorLeft.position.y,
          },
          right: {
            open: this.frontDoorRight.position.y - this.doorWidth,
            closed: this.frontDoorRight.position.y,
          },
        },
        backDoor: {
          left: {
            open: this.backDoorLeft.position.y + this.doorWidth,
            closed: this.backDoorLeft.position.y,
          },
          right: {
            open: this.backDoorRight.position.y - this.doorWidth,
            closed: this.backDoorRight.position.y,
          },
        },
        floor: {
          down: this.floorSeparatorIndicator.position.z,
          up: this.floorSeparatorIndicator.position.z + this.floorHeight,
        },
      };
    });

    // Setup lighting
    const ambientLight = new THREE.AmbientLight(0xcccccc, 1);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xeeeeee, 2);
    directionalLight.position.set(1, 1, 0).normalize();
    this.scene.add(directionalLight);

    // Setup renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(500, 500);
    this.renderer.setClearColor(0xffffff, 0.5);

    // On reload: replace the current scene, instead of creating a new one
    if (this.canvas.children.length > 3) {
      this.canvas.replaceChild(
        this.renderer.domElement,
        this.canvas.children[3]
      );
    } else {
      this.canvas.appendChild(this.renderer.domElement);
    }
  };

  /*
   * Functions
   */

  private animateScene = () => {
    requestAnimationFrame(this.animateScene);
    TWEEN.update();
    this.renderScene();
  };

  private renderScene = () => {
    this.renderer.render(this.scene, this.camera);
  };

  private changeMaterial = (
    node: THREE.Group,
    material: THREE.MeshStandardMaterial
  ) => {
    if (node && typeof node.traverse === "function") {
      node.traverse((childNode: THREE.LineSegments) => {
        if (childNode.type === "LineSegments") {
          childNode.visible = false;
        } else {
          childNode.material = material;
        }
      });
    }
  };

  private openFrontDoor = (skipAnimation = false) => {
    if (
      this.frontDoorLeft.position.y === this.positions.frontDoor.left.closed
    ) {
      if (skipAnimation) {
        this.frontDoorLeft.position.y = this.positions.frontDoor.left.open;
        this.frontDoorLeft.position.y = this.positions.frontDoor.right.open;
      }
      new TWEEN.Tween({
        leftDoor: this.frontDoorLeft.position.y,
        rightDoor: this.frontDoorRight.position.y,
      })
        .to(
          {
            leftDoor: this.positions.frontDoor.left.open,
            rightDoor: this.positions.frontDoor.right.open,
          },
          this.speed
        )
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate(({ leftDoor, rightDoor }) => {
          this.frontDoorLeft.position.y = leftDoor;
          this.frontDoorRight.position.y = rightDoor;
        })
        .start();
    }
  };

  private closeFrontDoor = (skipAnimation = false) => {
    if (this.frontDoorLeft.position.y === this.positions.frontDoor.left.open) {
      if (skipAnimation) {
        this.frontDoorLeft.position.y = this.positions.frontDoor.left.closed;
        this.frontDoorLeft.position.y = this.positions.frontDoor.right.closed;
      }
      new TWEEN.Tween({
        leftDoor: this.frontDoorLeft.position.y,
        rightDoor: this.frontDoorRight.position.y,
      })
        .to(
          {
            leftDoor: this.positions.frontDoor.left.closed,
            rightDoor: this.positions.frontDoor.right.closed,
          },
          this.speed
        )
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate(({ leftDoor, rightDoor }) => {
          this.frontDoorLeft.position.y = leftDoor;
          this.frontDoorRight.position.y = rightDoor;
        })
        .start();
    }
  };

  private openBackDoor = (skipAnimation = false) => {
    if (this.backDoorLeft.position.y === this.positions.backDoor.left.closed) {
      if (skipAnimation) {
        this.backDoorLeft.position.y = this.positions.backDoor.left.open;
        this.backDoorLeft.position.y = this.positions.backDoor.right.open;
      }
      new TWEEN.Tween({
        leftDoor: this.backDoorLeft.position.y,
        rightDoor: this.backDoorRight.position.y,
      })
        .to(
          {
            leftDoor: this.positions.backDoor.left.open,
            rightDoor: this.positions.backDoor.right.open,
          },
          this.speed
        )
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate(({ leftDoor, rightDoor }) => {
          this.backDoorLeft.position.y = leftDoor;
          this.backDoorRight.position.y = rightDoor;
        })
        .start();
    }
  };

  private closeBackDoor = (skipAnimation = false) => {
    if (this.backDoorLeft.position.y === this.positions.backDoor.left.open) {
      if (skipAnimation) {
        this.backDoorLeft.position.y = this.positions.backDoor.left.closed;
        this.backDoorLeft.position.y = this.positions.backDoor.right.closed;
      }
      new TWEEN.Tween({
        leftDoor: this.backDoorLeft.position.y,
        rightDoor: this.backDoorRight.position.y,
      })
        .to(
          {
            leftDoor: this.positions.backDoor.left.closed,
            rightDoor: this.positions.backDoor.right.closed,
          },
          this.speed
        )
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate(({ leftDoor, rightDoor }) => {
          this.backDoorLeft.position.y = leftDoor;
          this.backDoorRight.position.y = rightDoor;
        })
        .start();
    }
  };

  private startPulseArrowAnimation = (arrow) => {
    if (arrow) {
      let counter = 0;
      this.pulseInterval = setInterval(() => {
        const selectedIndex = counter % 10;
        for (let i = 0; i <= arrow.children.length; i++) {
          if (i === selectedIndex) {
            this.changeMaterial(arrow.children[i], this.materials.pulseOn);
          } else {
            this.changeMaterial(arrow.children[i], this.materials.pulseOff);
          }
        }
        counter++;
      }, 180);
    }
  };

  private stopPulseArrowAnimation = () => {
    if (this.pulseInterval !== null) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }

    if (this.arrowDown?.children?.length) {
      for (let i = 0; i < this.arrowDown.children.length; i++) {
        this.changeMaterial(
          this.arrowDown.children[i],
          this.materials.pulseOff
        );
        this.changeMaterial(this.arrowUp.children[i], this.materials.pulseOff);
      }
    }
  };

  private moveUp = () => {
    this.stopPulseArrowAnimation();
    this.hideNode(this.arrowDown);
    this.showNode(this.arrowUp);
    this.startPulseArrowAnimation(this.arrowUp);
    this.startAnimateFloorsIndicator("down");
    this.movingDirection = "down";
  };

  private moveDown = () => {
    this.stopPulseArrowAnimation();
    this.hideNode(this.arrowUp);
    this.showNode(this.arrowDown);
    this.startPulseArrowAnimation(this.arrowDown);
    this.startAnimateFloorsIndicator("up");
    this.movingDirection = "up";
  };

  private stopMoving = () => {
    this.hideNode(this.arrowUp);
    this.hideNode(this.arrowDown);
    this.stopPulseArrowAnimation();
    this.stopAnimateFloorsIndicator(this.movingDirection);
  };

  private startAnimateFloorsIndicator = (direction: "up" | "down") => {
    this.animateTheFloor = true;
    this.animateFloorsIndicator(direction);
  };

  private stopAnimateFloorsIndicator = (elevatorDirection: "down" | "up") => {
    if (this.floorAnimationTween !== null) {
      this.animateTheFloor = false;
      this.floorAnimationTween.stop();
      let easingAmount = 50;

      if (elevatorDirection === "down") {
        easingAmount = -easingAmount;
      }

      // Easy out the floor animation
      new TWEEN.Tween({
        topPos: this.floorSeparatorIndicator.position.z,
      })
        .to({
          topPos: this.floorSeparatorIndicator.position.z + easingAmount,
        })
        .easing(TWEEN.Easing.Cubic.Out)
        .onUpdate(({ topPos }) => {
          this.floorSeparatorIndicator.position.z = topPos;
        })
        .start();
      this.floorAnimationTween = null;
    }
  };

  private animateFloorsIndicator = (elevatorDirection: "up" | "down") => {
    const continuous = this.animateTheFloor;
    let startPos;
    let endPos;
    let movementSpeed = this.floorHeight * 10;

    if (elevatorDirection === "up") {
      if (this.floorSeparatorIndicator.position.z >= this.positions.floor.up) {
        startPos = this.positions.floor.down;
      }
      // If the floor separator indicator is in view, continue from there
      else {
        startPos = this.floorSeparatorIndicator.position.z;
        // Adjust speed based on distance
        movementSpeed =
          Math.abs(
            this.floorSeparatorIndicator.position.z - this.positions.floor.up
          ) * 10;
      }
      endPos = this.positions.floor.up;
    } else if (elevatorDirection === "down") {
      if (
        this.floorSeparatorIndicator.position.z <= this.positions.floor.down
      ) {
        startPos = this.positions.floor.up;
      }
      // If the floor separator indicator is in view, continue from there
      else {
        startPos = this.floorSeparatorIndicator.position.z;
        // Adjust speed based on distance
        movementSpeed =
          (this.floorSeparatorIndicator.position.z -
            this.positions.floor.down) *
          10;
      }
      endPos = this.positions.floor.down;
    }

    if (this.floorAnimationTween !== null) {
      this.floorAnimationTween.stop();
    }

    const tween = new TWEEN.Tween({
      topPos: startPos,
    })
      .to(
        {
          topPos: endPos,
        },
        movementSpeed
      )
      .onUpdate(({ topPos }) => {
        this.floorSeparatorIndicator.position.z = topPos;
      })
      .onComplete(() => {
        // Restart the animation for continuous effect
        if (continuous === true) {
          this.animateFloorsIndicator(elevatorDirection);
        }
      })
      .start();
    this.floorAnimationTween = tween;
  };

  private hideNode = (node: THREE.Group) => {
    if (node !== undefined) {
      node.traverse((childNode) => {
        childNode.visible = false;
      });
    }
  };

  private showNode = (node: THREE.Group) => {
    if (node !== undefined) {
      node.traverse((childNode) => {
        if (childNode.type === "LineSegments") {
          childNode.visible = false;
        } else {
          childNode.visible = true;
        }
      });
    }
  };

  private processSensorUpdate = (sensorData: ILiftStatusSensorDataResponse) => {
    let sensorValues = sensorData.values;

    let lastUpdate = 0;
    let sensors = [500, 501, 502, 503, 514, 515];

    sensors.forEach((value) => {
      lastUpdate = dayjs(sensorData.updated[value]).isAfter(dayjs(lastUpdate))
        ? sensorData.updated[value]
        : lastUpdate;
    });

    this.lastUpdate = lastUpdate;

    for (let sensorId in sensorValues) {
      // Elevator state (movement)
      switch (sensorId) {
        case "500":
          if (sensorValues[sensorId] === "D") {
            if (this.movementState !== "D") {
              this.moveDown();
            }
          } else if (sensorValues[sensorId] === "U") {
            if (this.movementState !== "U") {
              this.moveUp();
            }
          } else {
            this.stopMoving();
          }
          this.movementState = sensorValues[sensorId];
          break;

        // Current stop place
        case "515":
          this.currentStop = sensorValues[sensorId];
          break;

        // Front door
        case "502":
          if (
            sensorValues[sensorId] === "C" ||
            sensorValues[sensorId] === "-"
          ) {
            this.closeFrontDoor(false);
          } else if (
            sensorValues[sensorId] === "O" ||
            sensorValues[sensorId] === "+"
          ) {
            this.openFrontDoor(false);
          } else {
            this.closeFrontDoor(false);
          }
          break;

        // Rear door
        case "503":
          if (
            sensorValues[sensorId] === "C" ||
            sensorValues[sensorId] === "-"
          ) {
            this.closeBackDoor(false);
          } else if (
            sensorValues[sensorId] === "O" ||
            sensorValues[sensorId] === "+"
          ) {
            this.openBackDoor(false);
          } else {
            this.closeBackDoor(false);
          }
          break;

        // Last passed
        case "514":
          this.lastPassedStop = sensorValues[sensorId];
          break;
      }
    }
  };

  private hasBackDoor = (hasBackDoor: boolean) => {
    if (hasBackDoor) return;

    // Wait until the scene is loaded
    if (
      this.backDoor === undefined ||
      this.elevatorBackWall === undefined ||
      this.elevatorBackWallBeam === undefined
    ) {
      setTimeout(() => {
        this.hasBackDoor(hasBackDoor);
      }, 100);
      return;
    }

    this.hideNode(this.backDoor);
    this.showNode(this.elevatorBackWall);
    this.showNode(this.elevatorBackWallBeam);
  };

  private handleError = (error: ILiftStatusWebComponentError) => {
    if (error.type === "Socket" || error.type === "Token") {
      let errorMessageElement = document.createElement("div");

      if (error.type === "Socket") {
        errorMessageElement.append("Geen verbinding");
        errorMessageElement.classList.add("error-message");
        console.log(error.message);
      } else {
        errorMessageElement.append(error.message);
        errorMessageElement.classList.add("error-message");
      }

      this.canvas.append(errorMessageElement);
      for (const key in this.canvas.children) {
        if (
          this.canvas.children[key].className !== "error-message" &&
          this.canvas.children[key].classList !== undefined
        ) {
          this.canvas.children[key].classList.add("blur");
        }
      }

      this.clearTimer();
    }
  };

  private setTimer() {
    // The timer is used to update the relative timestamps
    this.timer = window.setInterval(() => {
      this.processSensorUpdate(this.store.state.currentSensorData);
    }, 20000);
  }

  private clearTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  connectedCallback() {
    this.clearTimer();
    this.setTimer();
  }

  disconnectedCallback() {
    this.scene.dispose();
    this.clearTimer();
  }

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
  }

  componentDidLoad() {
    this.initializeScene();
    this.animateScene();
    this.hasBackDoor(this.store.state.hasBackDoor);

    if (!isEmpty(this.store.state.error)) {
      this.handleError(this.store.state.error);
    }

    this.store.onChange("currentSensorData", (sensorData) => {
      this.processSensorUpdate(sensorData);
    });
  }

  componentDidUpdate() {
    this.animateScene();
  }

  render() {
    return (
      <Host>
        <div
          class="canvas"
          ref={(el) => {
            this.canvas = el;
          }}
        >
          <ls-indicator class="current-floor" sensor-id="515" id-key={this.idKey}></ls-indicator>
          <ls-indicator
            class="last-passed-floor"
            sensor-id="514"
            id-key={this.idKey}
          ></ls-indicator>
          <div class="last-update">{dayjs(this.lastUpdate).fromNow()}</div>
        </div>
        <slot></slot>
      </Host>
    );
  }
}
